/**
 * AppShell — the role-based ONE-app frame.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  OPTION-A NAV REBUILD (2026-06-13, Anil) — AVBP_DESIGN_LANGUAGE_AND_NAV_
 *  SPEC_v1 §4.2, owner-approved (AVBP_DESIGN_NAV_DECISIONS Q7). The old 62px
 *  icon-only rail is REPLACED by the finalised Option-A navigation:
 *
 *   Desktop ≥1100px : a 220px WHITE LABELLED sidebar — logo "Aayatan Veda"
 *                     at the top, grouped sections (Inbox · Work · Manage ·
 *                     Apps) with uppercase group labels, green active state,
 *                     and live count badges (bright-green pill).
 *   Mobile          : exactly 5 FIXED bottom tabs — Home · Inbox · Projects ·
 *                     Clients · More (no horizontal scroll, every target
 *                     ≥44px). Everything else lives in the "More" sheet
 *                     (one tap, alphabetical, badge-aware); account moves into
 *                     More.
 *   Both            : the violet AI dock (GlobalChatDock) on every screen.
 *
 *  The OLD icon rail + the horizontally-scrolling mobile tab strip are gone.
 *  Naming is the locked set (Team Inbox · Leads · Projects · Clients ·
 *  Partners · My Tasks · Add-on Services · Forms · Payments · Team Workload ·
 *  Approvals · Trash · Settings · HR ↗ · Capture ↗); "Conversation" dropped.
 * ─────────────────────────────────────────────────────────────────────────
 *
 *  Launcher tiles (HR / Capture) keep the login/launcher-hotfix behaviour:
 *  visible to every team member, a click opens `<url>?legacy=1` in a new tab
 *  (the destination app runs its own auth — no SSO mint).
 */
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  usePageChromeBreadcrumb,
  usePageChromeIdentity,
} from '../lib/pageChromeContext';
import { withLegacy } from '../lib/handoff';
import { devToolsEnabled } from '../lib/devFlags';
import {
  NAV,
  NAV_GROUPS,
  MOBILE_TAB_LABEL,
  mobilePrimaryKeys,
  pageTitleFor,
  setDemoRole,
  type NavItem,
  type Role,
} from '../lib/roles';
import {
  accountMenuReducer,
  type AccountMenuEvent,
  type AccountMenuState,
} from '../lib/accountMenu';
import GlobalChatDock from './GlobalChatDock';

const DEMO_TRACKS: Array<[Role, string]> = [
  ['admin', 'Admin'],
  ['staff', 'Staff'],
  ['client', 'Client'],
];

/**
 * Build-time label for the top-bar version block. `__BUILD_TIME__` is an ISO
 * string stamped fresh on every `vite build` (vite.config.ts `define`), so the
 * displayed timestamp can never be stale. Rendered in IST. Computed once at
 * module load (the build time is constant for the life of the bundle).
 */
const BUILD_LABEL = new Date(__BUILD_TIME__).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** External launcher base URLs, keyed by the nav item `key`. */
const LAUNCHER_URL: Record<string, string> = {
  hr: 'https://hr.aayatanveda.in/',
  capture: 'https://capture.aayatanveda.in/',
};

/** Open a sibling app with `?legacy=1` in a new tab (destination logs in). */
function launchLegacy(key: string): void {
  const base = LAUNCHER_URL[key];
  if (!base) return;
  window.open(withLegacy(base), '_blank', 'noopener,noreferrer');
}

/**
 * Live nav-badge counts. Wired to real signals in a later slice (unread chats,
 * new leads, queued approvals); for now the shell resolves an empty map so the
 * badge slot exists but shows nothing until the counts are piped in. Kept as a
 * single seam so the badge source drops in here without touching the markup.
 */
function useNavBadges(): Record<string, number> {
  // TODO(money/teaminbox slice): pull live unread/lead/approval counts.
  return useMemo(() => ({}), []);
}

export default function AppShell() {
  const { role, user, signOut } = useAuth();
  const r: Role = role ?? 'staff';
  const groups = NAV_GROUPS[r] ?? NAV_GROUPS.staff;
  const flat = NAV[r] ?? NAV.staff;
  const badges = useNavBadges();

  const loc = useLocation();
  const navigate = useNavigate();

  // Breadcrumb PUBLISHED by the current route (detail pages publish their
  // resolved trail, e.g. Work › Projects › {project name}). When set it OWNS
  // the top-left `.crumb` region; when null the route-derived crumb below is
  // used. This is the channel that lets ProjectDetail put the breadcrumb back
  // in the global chrome instead of inside its own sticky header.
  const pageBreadcrumb = usePageChromeBreadcrumb();

  // Identity PUBLISHED by the current route (detail pages publish their subject
  // line — client name + VP·tier chips + money/assignee meta). When set it fills
  // the VACANT centre of `.phead` (where the generic <h1> would be), so the
  // identity lives in the top chrome bar instead of a separate working-area
  // strip (owner ask 2026-06-28). When null the route <h1> renders as before.
  const pageIdentity = usePageChromeIdentity();

  // REFRESH-IN-PLACE (owner item 5) — re-runs the CURRENT route's data load by
  // remounting the keyed <Outlet>, without navigating (URL stays put).
  const [refreshKey, setRefreshKey] = useState(0);

  const showLauncher = r === 'admin' || r === 'staff';

  // Dev-only role-preview switcher (?dev=1 / persisted opt-in).
  const [showRoleSwitch] = useState(() => devToolsEnabled());
  function switchTrack(role2: Role) {
    setDemoRole(role2);
    window.location.assign('/');
  }

  // Mobile "More" bottom sheet open/close.
  const [moreOpen, setMoreOpen] = useState(false);
  // Close the More sheet whenever the route changes (a tap inside it navigates).
  useEffect(() => {
    setMoreOpen(false);
  }, [loc.pathname]);

  const initials =
    (user?.displayName || user?.email || 'You')
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || 'YOU';

  // Account-menu confirm gate (#915) — a stray click can never sign out.
  const [acct, dispatchAcctRaw] = useReducer(
    (s: AccountMenuState, e: AccountMenuEvent) => accountMenuReducer(s, e).next,
    'closed' as AccountMenuState,
  );
  const acctWrapRef = useRef<HTMLDivElement | null>(null);
  const acctSheetRef = useRef<HTMLDivElement | null>(null);

  function dispatchAcct(e: AccountMenuEvent) {
    const { doSignOut } = accountMenuReducer(acct, e);
    dispatchAcctRaw(e);
    if (doSignOut) {
      void (async () => {
        await signOut();
        navigate('/login', { replace: true });
      })();
    }
  }

  useEffect(() => {
    if (acct === 'closed') return;
    function onDoc(ev: MouseEvent) {
      const target = ev.target as Node;
      const insideDesktop =
        acctWrapRef.current && acctWrapRef.current.contains(target);
      const insideMobile =
        acctSheetRef.current && acctSheetRef.current.contains(target);
      const onAcctTrigger =
        target instanceof Element && target.closest('.acct-trigger') !== null;
      if (!insideDesktop && !insideMobile && !onAcctTrigger) {
        dispatchAcct('cancel');
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') dispatchAcct('cancel');
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acct]);

  const isActive = (path: string) => loc.pathname === path;

  // Render ONE sidebar nav item (in-portal route OR external launcher).
  function renderSideItem(item: NavItem) {
    const count = item.badgeKey ? badges[item.badgeKey] ?? 0 : 0;
    if (item.external) {
      if (!showLauncher) return null;
      return (
        <button
          key={item.key}
          type="button"
          className="nav-item nav-ext"
          title={`${item.label} — opens in a new tab`}
          aria-label={`Open ${item.label} in a new tab`}
          onClick={() => launchLegacy(item.key)}
        >
          <span className="nav-ic" aria-hidden>
            {item.icon}
          </span>
          <span className="nav-lbl">{item.label}</span>
          <span className="nav-ext-mark" aria-hidden>
            ↗
          </span>
        </button>
      );
    }
    return (
      <NavLink
        key={item.key}
        to={item.path}
        className={`nav-item ${isActive(item.path) ? 'on' : ''}`}
        aria-current={isActive(item.path) ? 'page' : undefined}
      >
        <span className="nav-ic" aria-hidden>
          {item.icon}
        </span>
        <span className="nav-lbl">{item.label}</span>
        {count > 0 && (
          <span
            className={`nav-badge ${item.badgeKey === 'approvals' ? 'crit' : ''}`}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </NavLink>
    );
  }

  /* ── Mobile 5-tab model ── */
  const frontKeys = mobilePrimaryKeys(r);
  const frontTabs = frontKeys
    .map((k) => flat.find((i) => i.key === k))
    .filter((i): i is NavItem => Boolean(i));
  // Everything NOT a front tab → the "More" sheet (in-portal items first,
  // alphabetical by label; then external launchers; account is appended in
  // the markup). The Home tab is always front, never in More.
  const moreItems = flat
    .filter((i) => !frontKeys.includes(i.key) && i.key !== 'home')
    .sort((a, b) => a.label.localeCompare(b.label));
  const moreLaunchers = showLauncher
    ? groups.flatMap((g) => g.items).filter((i) => i.external)
    : [];

  // PAGE TITLE (owner fix 2026-06-14) — every screen names itself. Resolves
  // nested + dynamic routes (rate card, project, client, partner, staff roles,
  // conversation…), never the brand name (so it can't duplicate the crumb).
  const routeTitle = pageTitleFor(loc.pathname, r);

  // BREADCRUMB (owner fix 2026-06-14) — replaces the doubled "Aayatan Veda
  // Portal" crumb. Shows WHERE you are: the nav SECTION the route lives under
  // (Inbox · Work · Manage · Apps), and for a settings sub-route the parent
  // "Settings". The brand name no longer appears here (it's the sidebar logo);
  // the page name is the <h1> below. Role/account context is a muted suffix.
  const isProjectRoute = loc.pathname.startsWith('/project/');
  const crumbSection: string | null = (() => {
    // Settings sub-routes (rate card / staff roles) read "Settings".
    if (loc.pathname.startsWith('/settings/')) return 'Settings';
    // Dynamic detail routes name their parent list section.
    if (loc.pathname.startsWith('/project/')) return 'Work';
    // Module 2 nav: /contacts/:id is in Manage; /client/:id is legacy (also Manage).
    if (loc.pathname.startsWith('/contacts/')) return 'Manage';
    if (loc.pathname.startsWith('/client/')) return 'Manage';
    if (loc.pathname.startsWith('/partner/')) return 'Work';
    // Module 4 nav: /properties/:id is in Manage.
    if (loc.pathname.startsWith('/properties/')) return 'Manage';
    // Otherwise: the labelled group that contains this exact top-level route.
    for (const g of groups) {
      if (!g.label) continue;
      if (g.items.some((it) => !it.external && it.path === loc.pathname)) {
        return g.label;
      }
    }
    return null;
  })();

  function mobileLabel(item: NavItem): string {
    return MOBILE_TAB_LABEL[item.key] ?? item.label;
  }

  // DEDICATED EVALUATION WORKSPACE (mockup v3.5 / decisions #1) — a FULL-SCREEN
  // focused space: NO portal sidebar, NO breadcrumb, NO admin/staff topbar, NO
  // mobile tabbar. Just the workspace, which carries its own two-row top + footer
  // and exits via "Back to project". Scoped strictly to the /evaluate route.
  if (loc.pathname.startsWith('/evaluate/')) {
    return (
      <div className="evalws-fullscreen">
        <Outlet key={`${loc.pathname}::${refreshKey}`} />
      </div>
    );
  }

  return (
    <div className="shell">
      {/* ════════════ Desktop labelled sidebar (Option A) ════════════ */}
      <nav className="sidebar" aria-label="Primary">
        <NavLink
          to="/"
          className="brandrow"
          aria-label="Aayatan Veda — go to Home"
          title="Aayatan Veda — Home"
        >
          <span className="brand-mark" aria-hidden>
            {/* Taupe AV monogram — byte-identical to the canonical
                Brand_Identity/svg/favicon_32_brand.svg. The old /av-mark.svg
                was filled #FFFFFF and rendered invisible on the white sidebar. */}
            <img src="/av-mark-brand.svg" alt="" />
          </span>
          <span className="brand-name">Aayatan Veda</span>
        </NavLink>

        <div className="nav-scroll">
          {groups.map((group, gi) => (
            <div
              key={group.label ?? `g${gi}`}
              className={`nav-group ${group.pinBottom ? 'pin-bottom' : ''}`}
            >
              {group.pinBottom && <div className="nav-spacer" />}
              {group.label && <div className="nav-glabel">{group.label}</div>}
              {group.items.map(renderSideItem)}
            </div>
          ))}
        </div>

        {/* Account — bottom of the sidebar (a labelled row, flyout on click). */}
        <div className="acct-wrap" ref={acctWrapRef}>
          <button
            className="nav-item acct-trigger"
            title="Account"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={acct !== 'closed'}
            onClick={() => dispatchAcct('toggle')}
          >
            <span className="nav-ic acct-ini" aria-hidden>
              {initials}
            </span>
            <span className="nav-lbl">{user?.displayName || 'Account'}</span>
          </button>
          {acct !== 'closed' && (
            <div className="acct-pop" role="menu" aria-label="Account">
              <div className="acct-id">
                <b>{user?.displayName || 'Signed in'}</b>
                {user?.email ?? ''}
              </div>
              {acct === 'open' && (
                <button
                  className="acct-act"
                  role="menuitem"
                  onClick={() => dispatchAcct('requestSignOut')}
                >
                  Sign out…
                </button>
              )}
              {acct === 'confirm' && (
                <div>
                  <p className="acct-confirm-q">
                    Sign out of the portal? You'll need to sign in again.
                  </p>
                  <div className="acct-confirm-row">
                    <button onClick={() => dispatchAcct('cancel')}>Cancel</button>
                    <button
                      className="danger"
                      onClick={() => dispatchAcct('confirmSignOut')}
                    >
                      Yes, sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* ════════════ Work area ════════════ */}
      <div className="work">
        <header className={isProjectRoute ? 'phead phead-project' : 'phead'}>
          {/* Breadcrumb in the ORIGINAL top-left chrome spot. Two sources:
              1) a route PUBLISHED trail (detail pages, e.g. a project →
                 Work › Projects › {name}) — it owns this region when set;
              2) otherwise the route-derived crumb (section › page) for every
                 normal list/landing route.
              On a project-detail route the identity (Client · VP · Tier) still
              lives in ProjectDetail's sticky header; its generic <h1> stays
              suppressed below so we never render "Project / Project" twice. The
              utility cluster (version/refresh/role/AI dock) is untouched. */}
          {pageBreadcrumb && pageBreadcrumb.length > 0 ? (
            <div className="crumb">
              <span className="crumb-trail">
                {pageBreadcrumb.map((seg, i) => {
                  const isLast = i === pageBreadcrumb.length - 1;
                  return (
                    <span
                      key={`${seg.label}-${i}`}
                      style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}
                    >
                      {i > 0 && (
                        <span className="crumb-sep" aria-hidden>
                          ›
                        </span>
                      )}
                      {seg.href && !isLast ? (
                        <NavLink to={seg.href} className="crumb-sec crumb-link">
                          {seg.label}
                        </NavLink>
                      ) : (
                        <span
                          className={isLast ? 'crumb-page' : 'crumb-sec'}
                          aria-current={isLast ? 'page' : undefined}
                        >
                          {seg.label}
                        </span>
                      )}
                    </span>
                  );
                })}
              </span>
              <span className="crumb-ctx">
                {r} · {user?.email ?? 'signed in'}
              </span>
            </div>
          ) : (
            !isProjectRoute && (
              <div className="crumb">
                <span className="crumb-trail">
                  {crumbSection && (
                    <>
                      <span className="crumb-sec">{crumbSection}</span>
                      <span className="crumb-sep" aria-hidden>
                        ›
                      </span>
                    </>
                  )}
                  <span className="crumb-page">{routeTitle}</span>
                </span>
                <span className="crumb-ctx">
                  {r} · {user?.email ?? 'signed in'}
                </span>
              </div>
            )
          )}
          <div className="ptitle">
            {/* VACANT-CENTRE IDENTITY (owner ask 2026-06-28) — when a route
                publishes an identity it OWNS the left of this row (where the
                generic <h1> used to sit): the subject title (client name) +
                inline chips (VP · tier pill) + compact meta (money chip +
                assignee), all on one line so `.phead` stays ≤2 lines (the
                breadcrumb is line 1). The utility cluster (version/refresh/
                role/AI dock) is unchanged on the right, pushed by `.pspace`.
                When nothing is published the route <h1> renders as before. */}
            {pageIdentity ? (
              <div className="phead-identity">
                {pageIdentity.title != null && (
                  <span className="phead-id-title">{pageIdentity.title}</span>
                )}
                {pageIdentity.chips != null && (
                  <span className="phead-id-chips">{pageIdentity.chips}</span>
                )}
                {pageIdentity.meta != null && (
                  <span className="phead-id-meta">{pageIdentity.meta}</span>
                )}
              </div>
            ) : (
              !isProjectRoute && <h1>{routeTitle}</h1>
            )}
            <div className="pspace" />
            {/* Version + build-time — auto-filled at build, never stale.
                Sits immediately before the refresh/account cluster. */}
            <div className="topver" title={`Build version ${__APP_VERSION__}`}>
              <span className="v">v{__APP_VERSION__}</span>
              <span className="ts">Built {BUILD_LABEL} IST</span>
            </div>
            <button
              type="button"
              className="phead-refresh"
              title="Refresh this page"
              aria-label="Refresh this page"
              onClick={() => setRefreshKey((n) => n + 1)}
            >
              <span aria-hidden>⟳</span>
              <span className="phead-refresh-lbl">Refresh</span>
            </button>
            {showRoleSwitch && (
              <div
                className="track-switch"
                role="group"
                aria-label="Preview track (dev tool)"
                title="Dev tool — walk the Admin / Staff / Client tracks from one sign-in. Not production identity."
              >
                {DEMO_TRACKS.map(([rr, label]) => (
                  <button
                    key={rr}
                    className={`ts-btn${r === rr ? ' on' : ''}`}
                    aria-pressed={r === rr}
                    onClick={() => switchTrack(rr)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <GlobalChatDock />
          </div>
        </header>

        <main className="pbody">
          <Outlet key={`${loc.pathname}::${refreshKey}`} />
        </main>
      </div>

      {/* ════════════ Mobile 5-tab bar (fixed, no scroll) ════════════ */}
      <nav className="tabbar" aria-label="Primary">
        {frontTabs.map((item) => {
          const count = item.badgeKey ? badges[item.badgeKey] ?? 0 : 0;
          return (
            <button
              key={item.key}
              className={`tb ${isActive(item.path) ? 'on' : ''}`}
              onClick={() => navigate(item.path)}
              aria-current={isActive(item.path) ? 'page' : undefined}
            >
              <span className="ic2" aria-hidden>
                {item.icon}
              </span>
              <span>{mobileLabel(item)}</span>
              {count > 0 && <span className="bdg">{count > 99 ? '99+' : count}</span>}
            </button>
          );
        })}
        {/* The fixed 5th tab — More. Opens the bottom sheet. */}
        <button
          className={`tb tb-more ${moreOpen ? 'on' : ''}`}
          onClick={() => setMoreOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-label="More"
        >
          <span className="ic2" aria-hidden>
            ≡
          </span>
          <span>More</span>
        </button>
      </nav>

      {/* ════════════ Mobile "More" sheet ════════════ */}
      {moreOpen && (
        <div
          className="more-wrap"
          role="dialog"
          aria-label="More"
          aria-modal="true"
        >
          <div className="more-scrim" onClick={() => setMoreOpen(false)} aria-hidden />
          <div className="more-sheet">
            <div className="more-grab" aria-hidden />
            <div className="more-title">More</div>
            <div className="more-grid">
              {moreItems.map((item) => {
                const count = item.badgeKey ? badges[item.badgeKey] ?? 0 : 0;
                return (
                  <button
                    key={item.key}
                    className="more-it"
                    onClick={() => navigate(item.path)}
                  >
                    <span className="more-ic" aria-hidden>
                      {item.icon}
                    </span>
                    <span className="more-lbl">{item.label}</span>
                    {count > 0 && <span className="more-bdg">{count > 99 ? '99+' : count}</span>}
                  </button>
                );
              })}
              {moreLaunchers.map((item) => (
                <button
                  key={item.key}
                  className="more-it"
                  onClick={() => launchLegacy(item.key)}
                  aria-label={`Open ${item.label} in a new tab`}
                >
                  <span className="more-ic" aria-hidden>
                    {item.icon}
                  </span>
                  <span className="more-lbl">{item.label} ↗</span>
                </button>
              ))}
              {/* Account moves into More on mobile (spec §4.2). */}
              <button
                className="more-it acct-trigger"
                onClick={() => {
                  setMoreOpen(false);
                  dispatchAcct('toggle');
                }}
                aria-label="Account"
              >
                <span className="more-ic acct-ini" aria-hidden>
                  {initials}
                </span>
                <span className="more-lbl">Account</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════ Mobile account sheet ════════════ */}
      {acct !== 'closed' && (
        <div
          className="acct-sheet-wrap"
          ref={acctSheetRef}
          role="dialog"
          aria-label="Account"
          aria-modal="true"
        >
          <div
            className="acct-sheet-scrim"
            onClick={() => dispatchAcct('cancel')}
            aria-hidden
          />
          <div className="acct-sheet">
            <div className="acct-sheet-grab" aria-hidden />
            <div className="acct-id">
              <b>{user?.displayName || 'Signed in'}</b>
              {user?.email ?? ''}
            </div>
            {acct === 'open' && (
              <button
                className="acct-act"
                onClick={() => dispatchAcct('requestSignOut')}
              >
                Sign out…
              </button>
            )}
            {acct === 'confirm' && (
              <div>
                <p className="acct-confirm-q">
                  Sign out of the portal? You'll need to sign in again.
                </p>
                <div className="acct-confirm-row">
                  <button onClick={() => dispatchAcct('cancel')}>Cancel</button>
                  <button
                    className="danger"
                    onClick={() => dispatchAcct('confirmSignOut')}
                  >
                    Yes, sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
